import { Nullable, hasValue } from '@frontend/sports/common/core/utils/extended-types';
import { intersection } from 'lodash-es';

import { CriteriaType, IRewardToken } from '../../reward-tokens.model';
import { IRewardTokenEligibility } from '../../state';
import { CriteriaValidatorBase } from './criteria-validator-base.service';

export abstract class TokenValidatorBase<T extends IRewardToken, S> {
    protected hardCriteriaValidators: CriteriaValidatorBase<T, CriteriaType, S>[] = [];
    protected softCriteriaValidators: CriteriaValidatorBase<T, CriteriaType, S>[] = [];

    validate(token: T, state: S): IRewardTokenEligibility {
        const hardCriteriaValidity = this.getInvalidHardCriteria(token, state);
        const softCriteriasValidity = this.getSoftCriteriasValidity(token, state);
        const isEligible = !hardCriteriaValidity.invalidHardCriteria;
        const eligiblePicks = hardCriteriaValidity.eligiblePicks;

        return {
            tokenId: token.userTokenId,
            isEligible,
            invalidHardCriteria: hardCriteriaValidity.invalidHardCriteria,
            softCriteriasValidity,
            eligiblePicksIds: eligiblePicks,
        };
    }

    private getInvalidHardCriteria(token: T, state: S): { invalidHardCriteria: Nullable<CriteriaType>; eligiblePicks: string[] } {
        const allCriteriaValidity = this.hardCriteriaValidators.map((criteria) => {
            const tokenValidity = criteria.isValid(token, state);
            if (typeof tokenValidity === 'boolean') {
                return { isValid: tokenValidity, type: criteria.type };
            }

            return { isValid: tokenValidity.isValid, type: criteria.type, validPicksIds: tokenValidity.validPicksIds };
        });

        const firstNotValidCriteria = allCriteriaValidity.find((tokenValidity) => !tokenValidity.isValid);

        //Find picks which are eligible by all hardCriteriaValidators
        const eligiblePicks = allCriteriaValidity
            .filter((validity) => hasValue(validity.validPicksIds))
            .map((validity) => validity.validPicksIds!)
            .reduce((previous, next) => intersection(previous, next), this.getBetslipPicksIds(state));

        return { invalidHardCriteria: firstNotValidCriteria ? firstNotValidCriteria.type : null, eligiblePicks };
    }

    private getSoftCriteriasValidity(token: T, state: S): { [key: string]: boolean } {
        const softCriteria: Record<CriteriaType, boolean> = {} as Record<CriteriaType, boolean>;

        this.softCriteriaValidators.forEach((criteria) => (softCriteria[criteria.type] = criteria.isValid(token, state) as boolean));

        return softCriteria;
    }

    abstract getBetslipPicksIds(state: S): string[];
}
